/**
 * panel.ts — Photo panel builder for pdfmake.
 *
 * Builds the Panel Fotográfico section as a 2-column photo grid.
 * Extracted from builder/index.ts (was inline in buildIeContent, P0).
 *
 * Each photo card is a single pdfmake table:
 *   Row 0: image (colSpan 2, cover-cropped to CARD_W×CARD_H) or gray placeholder (#eef0f5)
 *   Row 1: navy tag box (col 0) | title + description (col 1)
 *
 * Images use pdfmake's `cover` (like CSS object-fit: cover) so every slot renders at
 * the SAME fixed height regardless of source aspect ratio — no more uneven rows.
 *
 * The card uses ONE table so image row and caption row share the same outer width.
 * This avoids the alignment mismatch that occurs with a canvas + separate table.
 *
 * buildPhotoPanel accepts a sectionNum parameter so callers can pass "5" or "6"
 * depending on document type (IE = §6, IS/AC = §5).
 */
import type { Content } from "pdfmake/interfaces.js";
import type { VmFoto, Theme } from "../types.js";
import { autoContrast, subtleBorder } from "./color.js";

const PLACEHOLDER_COLOR = "#eef0f5";
const BORDER_COL = subtleBorder("#ffffff");
const CARD_H = 150;
const TAG_W = 26;
// Max image width inside a card so it never overflows the cell.
// content width = A4 595.28 − margins (72+72) − columnGap 12 → /2 ≈ 219.6, minus
// the table cell padding (~8) ≈ 211. Use 210 so the image is width-bounded to the box.
// (A wider card, e.g. 240, would not fit two-per-row on A4.)
const CARD_W = 210;

/**
 * A single photo card (image box + caption row inside one table).
 *
 * @param dataUri     - data URI (jpeg/png) or null/empty for gray placeholder
 * @param titulo      - caption title (bold)
 * @param descripcion - caption description
 * @param tag         - navy tag label, e.g. "6.1"
 * @param theme       - brand theme (primary color for tag bg)
 */
export function buildPhotoCard(
  dataUri: string | null,
  titulo: string,
  descripcion: string,
  tag: string,
  theme: Theme,
): Content {
  const hasValidImage =
    Boolean(dataUri) &&
    (dataUri as string).length > 200 &&
    (dataUri as string).startsWith("data:image/");

  // Row 0: image (colSpan 2) or empty cell (placeholder fill via fillColor).
  // `cover` crops the image to fill CARD_W×CARD_H (object-fit: cover) so all slots
  // share one fixed height. pdfmake 0.2.x supports it; interfaces.d.ts lacks the type.
  const imageCell: Content = hasValidImage
    ? ({
        image: dataUri!,
        cover: { width: CARD_W, height: CARD_H, valign: "center", align: "center" },
        colSpan: 2,
      } as unknown as Content)
    : ({ text: "", margin: [0, CARD_H / 2, 0, CARD_H / 2], colSpan: 2 } as unknown as Content);

  return {
    table: {
      widths: [TAG_W, "*"],
      headerRows: 0,
      dontBreakRows: true,
      body: [
        [imageCell, {}],
        [
          { text: tag, bold: true, fontSize: 8, color: autoContrast(theme.primary), alignment: "center" },
          {
            stack: [
              { text: titulo, bold: true, fontSize: 8, color: "#1a1a1a" },
              { text: descripcion, fontSize: 7, color: "#555555" },
            ],
            margin: [2, 1, 2, 1],
          },
        ],
      ],
    },
    layout: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fillColor: (row: number, _node: any, col: number) => {
        if (row === 0) return hasValidImage ? null : PLACEHOLDER_COLOR;
        return col === 0 ? theme.primary : null;
      },
      hLineWidth: () => 0.5,
      hLineColor: () => BORDER_COL,
      vLineWidth: () => 0.5,
      vLineColor: () => BORDER_COL,
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 3,
      paddingBottom: () => 3,
    },
  } as unknown as Content;
}

/**
 * Build the 2-column photo grid for the Panel Fotográfico section.
 *
 * Each pair of photos becomes one row; an odd trailing photo gets an empty right slot.
 * Tags are numbered sequentially: "§.1", "§.2", ... using the provided sectionNum.
 *
 * @param fotos      - array of VmFoto (dataUri="" → gray placeholder)
 * @param theme      - brand theme
 * @param sectionNum - section number prefix for tags (default "6" for IE; pass "5" for IS/AC)
 */
export function buildPhotoPanel(fotos: VmFoto[], theme: Theme, sectionNum: string): Content {
  const rows: Content[][] = [];
  for (let i = 0; i < fotos.length; i += 2) {
    const left = fotos[i]!;
    const right = fotos[i + 1];
    const row: Content[] = [
      buildPhotoCard(
        left.dataUri || null,
        left.titulo,
        left.descripcion,
        `${sectionNum}.${i + 1}`,
        theme,
      ),
      right
        ? buildPhotoCard(
            right.dataUri || null,
            right.titulo,
            right.descripcion,
            `${sectionNum}.${i + 2}`,
            theme,
          )
        : ({ text: "", width: "*" } as Content),
    ];
    rows.push(row);
  }

  return {
    stack: rows.map(
      (row) =>
        ({
          columns: row,
          columnGap: 12,
          margin: [0, 0, 0, 8],
          // Keep both photos in a pair together: if the row doesn't fit on the current
          // page, pdfmake moves the WHOLE row to the next page instead of splitting it.
          unbreakable: true,
        }) as Content,
    ),
    margin: [0, 0, 0, 12],
  } as Content;
}
